import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardContent,
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonBadge,
    IonModal,
    IonGrid,
    IonRow,
    IonCol,
    IonText,
    useIonRouter,
} from '@ionic/react';
import { list, calendarOutline, briefcaseOutline, checkmarkCircle, alertCircle, calendar } from 'ionicons/icons';
import styles from './StaffJobs.module.scss';
import JobsListModal from '../components/JobsListModal';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { useAuth } from '../contexts';
import { useTranslation } from 'react-i18next';

const StaffJobs: React.FC = () => {
  const { t } = useTranslation();
    const router = useIonRouter();
    const { authState } = useAuth();
    const { user } = authState;
    const [showJobsListModal, setShowJobsListModal] = useState(false);

    const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
    const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
    const titleCardElementRef = useRef<HTMLDivElement>(null);
    const contentElementRef = useRef<HTMLIonGridElement>(null);

    const [jobStats, setJobStats] = useState({
        total: 0,
        confirmed: 0,
        pending: 0,
        completed: 0,
    });

    const fetchJobStats = useCallback(async () => {
        try {
            const dataService = DataServiceFactory.getService();
            if (!dataService) return;

            const today = new Date();
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(today.getDate() - 30);
            const thirtyDaysFromNow = new Date();
            thirtyDaysFromNow.setDate(today.getDate() + 30);

            const bookings = await dataService.bookings.getBookingsAndBlockedSlotsByDateRange(
                thirtyDaysAgo,
                thirtyDaysFromNow,
                []
            );

            if (!Array.isArray(bookings)) return;

            const staffName = user?.name || user?.firstName || '';
            const myJobs = staffName
                ? bookings.filter((b: BookingItem) => b.staff && b.staff.includes(staffName))
                : bookings;

            setJobStats({
                total: myJobs.length,
                confirmed: myJobs.filter((b: BookingItem) => b.status?.toLowerCase() === 'confirmed').length,
                pending: myJobs.filter((b: BookingItem) => b.status?.toLowerCase() === 'pending').length,
                completed: myJobs.filter((b: BookingItem) => b.status?.toLowerCase() === 'completed').length,
            });
        } catch (error) {
            console.error('Error fetching job stats:', error);
        }
    }, [user]);

    useEffect(() => {
        Promise.resolve().then(() => fetchJobStats());
    }, [fetchJobStats]);

    const applyProgress = useCallback((progress: number, _smooth: boolean) => {
        baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
        if (titleCardAnimationRef.current) {
            titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
            titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
        }
    }, []);

    const handleScroll = useScrollSnap(contentElementRef, 66, applyProgress);

    return (
        <IonPage>
            <BasePageWithHeader
                ref={baseHeaderRef}
                title={t('pages.StaffJobs.myJobs')}
                toolbarClassName={styles.toolbar}
                smallTitleClassName={styles.smallTitle}
                titleHeaderClassName={styles.titleHeader}
            >
                <IonContent scrollY={false}>
                    <IonGrid
                        ref={contentElementRef}
                        className={styles.jobsContainer}
                        onScroll={(e) => {
                            const top = (e.target as HTMLElement).scrollTop || 0;
                            handleScroll(top);
                        }}
                    >
                        <div ref={titleCardElementRef}>
                            <TitleCard
                                ref={titleCardAnimationRef}
                                title={t('pages.StaffJobs.myJobs')}
                                cardClassName={styles.jobsOverviewCard}
                                titleContainerClassName={styles.titleContainer}
                                titleClassName={styles.scoreTitle}
                            >
                                <IonGrid>
                                    <IonRow className="ion-align-items-center">
                                        <IonCol size="auto">
                                            <div className={styles.scoreCircle}>
                                                <IonIcon icon={briefcaseOutline} className={styles.scoreIcon} />
                                            </div>
                                        </IonCol>
                                        <IonCol>
                                            <IonText>
                                                <h1 className={styles.scoreValue}>{jobStats.total}</h1>
                                                <p className={styles.scoreDescription}>{t('pages.StaffJobs.jobsInTheLast30Days')}
                                                </p>
                                            </IonText>
                                        </IonCol>
                                    </IonRow>
                                </IonGrid>

                                <div className={styles.jobsChecklist}>
                                    <div className={styles.checklistItem}>
                                        <IonIcon icon={checkmarkCircle} color="success" className={styles.checklistIcon} />
                                        <IonText>
                                            <p className={styles.checklistText}>{t('pages.StaffJobs.confirmed', { confirmed: jobStats.confirmed })}</p>
                                        </IonText>
                                    </div>
                                    <div className={styles.checklistItem}>
                                        <IonIcon icon={alertCircle} color="warning" className={styles.checklistIcon} />
                                        <IonText>
                                            <p className={styles.checklistText}>{t('pages.StaffJobs.pending', { pending: jobStats.pending })}</p>
                                        </IonText>
                                    </div>
                                    <div className={styles.checklistItem}>
                                        <IonIcon icon={checkmarkCircle} color="medium" className={styles.checklistIcon} />
                                        <IonText>
                                            <p className={styles.checklistText}>{t('pages.StaffJobs.completed', { completed: jobStats.completed })}</p>
                                        </IonText>
                                    </div>
                                </div>
                            </TitleCard>
                        </div>

                        {/* Jobs List */}
                        <IonCard className={styles.sectionCard}>
                            <IonCardHeader>
                                <IonCardTitle className={styles.sectionTitle}>
                                    <IonIcon icon={list} className={styles.sectionIcon} />{t('pages.StaffJobs.jobsList')}
                                </IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <IonText color="medium">
                                    <p className={styles.sectionDescription}>{t('pages.StaffJobs.viewAndSearchThroughAllYour')}
                                    </p>
                                </IonText>
                                <IonList className={styles.settingsList}>
                                    <IonItem
                                        button
                                        detail
                                        lines="none"
                                        onClick={() => setShowJobsListModal(true)}
                                        className={styles.settingsItem}
                                    >
                                        <IonIcon icon={list} slot="start" />
                                        <IonLabel>
                                            <h3>{t('pages.StaffJobs.viewAllJobs')}</h3>
                                            <p>{t('pages.StaffJobs.searchFilterAndReviewYourJobs')}</p>
                                        </IonLabel>
                                        <IonBadge slot="end" color="primary">{jobStats.total}</IonBadge>
                                    </IonItem>
                                </IonList>
                            </IonCardContent>
                        </IonCard>

                        {/* Calendar View */}
                        <IonCard className={styles.sectionCard}>
                            <IonCardHeader>
                                <IonCardTitle className={styles.sectionTitle}>
                                    <IonIcon icon={calendar} className={styles.sectionIcon} />{t('pages.StaffJobs.calendarView')}
                                </IonCardTitle>
                            </IonCardHeader>
                            <IonCardContent>
                                <IonText color="medium">
                                    <p className={styles.sectionDescription}>{t('pages.StaffJobs.viewYourJobsInACalendar')}
                                    </p>
                                </IonText>
                                <IonList className={styles.settingsList}>
                                    <IonItem
                                        button
                                        detail
                                        lines="none"
                                        onClick={() => router.push('/tabs/my-schedule')}
                                        className={styles.settingsItem}
                                    >
                                        <IonIcon icon={calendarOutline} slot="start" />
                                        <IonLabel>
                                            <h3>{t('pages.StaffJobs.openCalendar')}</h3>
                                            <p>{t('pages.StaffJobs.navigateToCalendarView')}</p>
                                        </IonLabel>
                                    </IonItem>
                                </IonList>
                            </IonCardContent>
                        </IonCard>
                    </IonGrid>
                </IonContent>

                <IonModal
                    isOpen={showJobsListModal}
                    onDidDismiss={() => setShowJobsListModal(false)}
                    onWillDismiss={() => setShowJobsListModal(false)}
                    canDismiss={true}
                    backdropDismiss={true}
                    keepContentsMounted={false}
                    handle={false}
                    className={styles.fullscreenModal}
                >
                    <JobsListModal onClose={() => setShowJobsListModal(false)} />
                </IonModal>
            </BasePageWithHeader>
        </IonPage>
    );
};

export default StaffJobs;
